/**
 * PATH: packages/document-editor/src/DocumentEditor.tsx
 * FILE_ID: DOCEDITOR-FRAME-001
 * PURPOSE: Frame assembling editor lifecycle, toolbar, and paginated A4 shell.
 * WHY: Consumers mount one JSON-first editor while injecting domain extensions.
 * RELATED: useDocumentEditor.ts, PaginatedPageShell.tsx
 */
import { EditorContent } from '@tiptap/react'
import { DOCUMENT_EDITOR_CSS } from './extensions'
import { DocumentToolbar } from './DocumentToolbar'
import { PaginatedPageShell } from './PaginatedPageShell'
import {
  useDocumentEditorFrame,
  type DocumentEditorProps,
  type ExactPdfPreviewRequest,
} from './useDocumentEditorFrame'

export type { DocumentEditorProps, ExactPdfPreviewRequest }

export function DocumentEditor(props: DocumentEditorProps) {
  const frame = useDocumentEditorFrame(props)
  if (props.loading) return <div role="status">Loading document editor…</div>
  if (props.error) return <div role="alert">Document editor unavailable: {props.error}</div>
  return (
    <div className="qdoc-editor">
      <style>{DOCUMENT_EDITOR_CSS}</style>
      <PaginatedPageShell
        pageSettings={frame.pageSettings}
        zoom={frame.zoom}
        navigation={{ activePage: frame.activePage, pageCount: frame.pagination.pageCount }}
        onZoomChange={frame.setZoom}
        onPageChange={frame.pagination.scrollToPage}
        onPageSettingsChange={props.readOnly ? undefined : frame.changePageSettings}
        onOpenExactPdfPreview={props.onOpenExactPdfPreview ? frame.openExactPdfPreview : undefined}
        paperRef={frame.pagination.paperRef}
        viewportRef={frame.pagination.viewportRef}
        toolbar={props.readOnly ? undefined : (
          <DocumentToolbar
            editor={frame.editor}
            onSelectImage={props.onSelectImage}
            domainActions={props.domainToolbarActions}
          />
        )}
      >
        <EditorContent editor={frame.editor} />
      </PaginatedPageShell>
    </div>
  )
}
